import { AccountInfo, InteractionStatus, InteractionRequiredAuthError, AuthenticationResult } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useState } from "react";
import jwt_decode from "jwt-decode";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import moment from "moment";
import Routes from "./Routes";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
  dialogTitle: {
    color: "#592C82",
  },
});

export default function AlertDialog() {
  const classes = useStyles();
  const [expiration, setExpiration] = useState<any>();
  const [secondsRemaining, setSecondsRemaining] = useState<any>();
  const [open, setOpen] = useState(false);
  const accountInfo: AccountInfo | null = useAccount();
  const { instance, inProgress } = useMsal();

  const refreshSession = useCallback(() => {
    var request = {
      scopes: [],
    };

    instance.acquireTokenSilent(request).then(tokenResponse => { 
      setNewExpiration(tokenResponse);
    })
    .catch(async (error) => {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interaction when silent call fails.
        const tokenResponse = await instance.acquireTokenPopup(request);
        setNewExpiration(tokenResponse);
      }});
    }, [instance]);

  const setNewExpiration = (tokenResponse: AuthenticationResult) =>
  {
    const accessToken: any = jwt_decode(tokenResponse.accessToken);
    const tokenExpiration = moment.unix(accessToken.exp);
    setExpiration(tokenExpiration);
  }

  const handleSignIn = async () => {
    await refreshSession();
    setOpen(false);
  };

  const handleSignOut = useCallback(async () => {

    // To have a silent logout MS Entra needs to set a "login_hint" on the token claims. For details about this
    // see https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md#option-2-manually-set-the-logouthint-option-in-the-logout-request
    const logoutHint = accountInfo?.idTokenClaims?.login_hint;

    instance.logoutRedirect({ account: accountInfo, postLogoutRedirectUri : window.location.origin + Routes.logout, logoutHint: logoutHint });
  }, [instance, accountInfo]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accountInfo) {
      refreshSession();
    }
  }, [inProgress, accountInfo, refreshSession]);

  useEffect(() => {
    let interval = setInterval(() => {}, 60000);
    if (expiration) {
      interval = setInterval(() => {
        const duration = moment.duration(expiration.diff(new Date()));
        const seconds = Math.trunc(duration.asSeconds());
        setSecondsRemaining(seconds);
        if (seconds <= 0) {
          handleSignOut();
        } else if (seconds <= 60) {
          setOpen(true);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [expiration, handleSignOut]);

  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle className={classes.dialogTitle} id="alert-dialog-title">
        {"Your session is about to expire!"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          You will be logged out in{" "}
          <strong>
            {secondsRemaining} seconds. Do you want to stay signed in?
          </strong>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSignOut} disableRipple variant="outlined">
          Sign Me Out
        </Button>
        <Button
          onClick={handleSignIn}
          disableRipple
          variant="contained"
          disableElevation
        >
          Keep Me Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
}
